import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Briefcase, Users, ClipboardCheck, BarChart3, SlidersHorizontal, Paperclip, ArrowUp } from "lucide-react";
import { ComposerSettings, DEFAULT_CONFIG, changedCount } from "@/components/home/ComposerSettings";
import { AttachJdDialog } from "@/components/home/AttachJdDialog";

export const MODES = [
  { key: "Create Job", label: "New role", icon: Briefcase, placeholder: "Hire a SOC Analyst L2 in Muscat, on site, 2–4 years of SIEM experience…" },
  { key: "Find Talent", label: "Find talent", icon: Users, placeholder: "Find backend engineers with Kafka and payments experience…" },
  { key: "Assessment", label: "Assessment", icon: ClipboardCheck, placeholder: "Build a 45-minute assessment for our SOC Analyst role…" },
  { key: "Analytics", label: "Ask about data", icon: BarChart3, placeholder: "How is our hiring funnel this month?" },
];
const DEPTHS = ["Quick draft", "Standard", "Detailed"];

export function HomeComposer({ onSubmit, userId }) {
  const storeKey = `hs_composer_defaults_${userId || "anon"}`;
  const [text, setText] = useState("");
  const [mode, setMode] = useState(MODES[0].key);
  const [depth, setDepth] = useState("Standard");
  const [open, setOpen] = useState(false);
  const [attach, setAttach] = useState(false);
  const [config, setConfig] = useState(() => { try { return { ...DEFAULT_CONFIG, ...JSON.parse(localStorage.getItem(storeKey) || "{}") }; } catch { return DEFAULT_CONFIG; } });
  const ref = useRef(null);
  const current = MODES.find((m) => m.key === mode);
  const changed = changedCount(config);
  useEffect(() => { ref.current?.focus(); }, [mode]);

  const submit = (value = text, m = mode) => { if (!value.trim()) return; onSubmit(value, { mode: m, depth, config }); };
  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } };

  return (
    <div className="hs-composer" data-testid="home-composer">
      <label htmlFor="home-composer-input" className="sr-only">Describe what you need</label>
      <textarea id="home-composer-input" ref={ref} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} rows={2}
        placeholder={current.placeholder} data-testid="home-composer-input"
        className="w-full resize-none bg-transparent px-5 sm:px-6 pt-5 pb-1 text-[16px] sm:text-[17px] leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none min-h-[72px]" />
      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3.5 pt-2">
        <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="What do you want to do">
          {MODES.map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" role="radio" aria-checked={mode === key} onClick={() => setMode(key)} data-testid={`composer-mode-${label.replace(/\s+/g, "-").toLowerCase()}`}
              className={cn("hs-chip", mode === key && "hs-chip-active")}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
        {open && mode === "Create Job" && (
          <div className="hidden md:flex items-center p-0.5 rounded-full bg-foreground/[0.05]" data-testid="composer-depth">
            {DEPTHS.map((d) => <button key={d} type="button" onClick={() => setDepth(d)} data-testid={`composer-depth-${d.split(" ")[0].toLowerCase()}`} className={cn("px-3 py-1.5 rounded-full text-[13px] transition-colors", depth === d ? "bg-foreground/[0.1] text-foreground font-semibold" : "text-muted-foreground hover:text-foreground")}>{d}</button>)}
          </div>
        )}
        <div className="flex-1" />
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="composer-settings-toggle"
          className={cn("flex items-center gap-1.5 text-[13px] px-2.5 py-1.5 rounded-lg transition-colors", changed || open ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground")}>
          <SlidersHorizontal className="h-3.5 w-3.5" /> Settings{changed ? ` · ${changed} changed` : ""}
        </button>
        <button type="button" onClick={() => setAttach(true)} data-testid="composer-attach-jd" className="hidden sm:flex items-center gap-1.5 text-[13px] px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors">
          <Paperclip className="h-3.5 w-3.5" /> Attach JD
        </button>
        <button type="button" onClick={() => submit()} disabled={!text.trim()} aria-label="Send" data-testid="home-composer-send"
          className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center transition-[transform,opacity] hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100">
          <ArrowUp className="h-[18px] w-[18px]" />
        </button>
      </div>
      {open && <ComposerSettings config={config} setConfig={setConfig} onSaveDefault={() => { try { localStorage.setItem(storeKey, JSON.stringify(config)); } catch { /* ignore */ } }} />}
      <AttachJdDialog open={attach} onOpenChange={setAttach} onUse={(jd) => submit(`Create a role from this job description:\n\n${jd}`, "Create Job")} />
    </div>
  );
}
